import { Command, Option } from "clipanion";
import { AzureCliCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { getAzAccount } from "../../lib/auth.js";
import { awaitDataPlaneReady } from "../../lib/data-plane-ready.js";
import { makeAgentsProbe } from "../../lib/foundry-ready-probe.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";

export class FoundryAwaitReadyCommand extends M8tCommand {
  static paths = [["foundry", "await-ready"]];
  static usage = Command.Usage({
    description: "Wait until a freshly-created Foundry project's data plane reliably serves it.",
    details:
      "Probes the project (GET /agents) until it returns 200 on a few consecutive tries, or fails clearly after a bounded budget. A newly-created account can serve intermittent 404 'Project not found' for minutes; run this after 'foundry create' and before deploying agents so the worker phase doesn't catch the unstable window.",
    examples: [["Wait for a project to be ready", "$0 foundry await-ready --endpoint https://acc.services.ai.azure.com/api/projects/m8t"]],
  });

  endpoint = Option.String("--endpoint");
  consecutive = Option.String("--consecutive", "3");
  attempts = Option.String("--attempts", "60");
  interval = Option.String("--interval", "5");
  subscription = Option.String("--subscription");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(this.output as "pretty" | "json" | "auto" | undefined, this.context.stdout as NodeJS.WriteStream);
    const endpoint = typeof this.endpoint === "string" ? this.endpoint : undefined;
    if (!endpoint) {
      throw new LocalCliError({ code: "USAGE", message: "--endpoint is required.", hint: "Example: m8t foundry await-ready --endpoint https://<acct>.services.ai.azure.com/api/projects/m8t" });
    }
    const consecutive = typeof this.consecutive === "string" ? Number(this.consecutive) : 3;
    const attempts = typeof this.attempts === "string" ? Number(this.attempts) : 60;
    const intervalMs = (typeof this.interval === "string" ? Number(this.interval) : 5) * 1000;

    await getAzAccount(); // ensures `az login` (throws a clear error otherwise)
    const credential = new AzureCliCredential();
    const probe = makeAgentsProbe(credential, endpoint);
    const onProgress = mode === "pretty" ? (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`) : undefined;

    const { ready, attempts: used } = await awaitDataPlaneReady({ probe, consecutive, attempts, intervalMs, onProgress });
    if (!ready) {
      throw new LocalCliError({
        code: "FOUNDRY_NOT_READY",
        message: `Foundry data plane did not stabilise for ${endpoint} within ${attempts.toString()} attempts.`,
        hint: "The Foundry data plane may be having a transient instability window — wait a few minutes and re-run, or re-run 'm8t bootstrap launch'.",
      });
    }
    if (mode === "json") { this.context.stdout.write(renderJson({ ready: true, attempts: used }) + "\n"); return 0; }
    this.context.stdout.write(`✓ Foundry data plane ready (${used.toString()} probes).\n`);
    return 0;
  }
}
